import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import AppLayout from '../../components/AppLayout';
import { S, Icon, PageHeader, BtnPrimary, BtnSecondary, TH, TD, formatDate } from '../regureStyles';

const BASE_URL = process.env.REACT_APP_BASE_URL || '';

// const FALLBACK_HOOKS = [
//   { _id: 'wh_01', name: 'Broker Portal — Claim Events', url: 'https://portal.broker-example.ch/webhooks/claims', events: ['claim.created', 'claim.status_changed', 'claim.assigned'], isActive: true, consecutiveFailures: 0, deliveryLog: [] },
//   { _id: 'wh_02', name: 'FNOL External System', url: 'https://fnol.insurer.ch/api/intake', events: ['claim.created'], isActive: true, consecutiveFailures: 0, deliveryLog: [] },
//   { _id: 'wh_03', name: 'Finance ERP Integration', url: 'https://erp.regure-internal.ch/webhooks', events: ['invoice.created', 'invoice.paid', 'commission.calculated'], isActive: true, consecutiveFailures: 0, deliveryLog: [] },
//   { _id: 'wh_04', name: 'Legacy Notification System', url: 'https://old-notify.example.ch/api', events: ['claim.created'], isActive: false, consecutiveFailures: 10, deliveryLog: [] },
// ];

const ALL_EVENTS = ['claim.created','claim.status_changed','claim.assigned','claim.closed','policy.issued','policy.renewed','invoice.created','invoice.paid','commission.calculated','dsar.created','audit.event'];

const WebhooksScreen = () => {
  const { t } = useTranslation();
  const [webhooks, setWebhooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState('');
  const [activeTab, setActiveTab] = useState('webhooks');
  const [showNewForm, setShowNewForm] = useState(false);
  const [newUrl, setNewUrl] = useState('');
  const [newName, setNewName] = useState('');
  const [selectedEvents, setSelectedEvents] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const fetchWebhooks = useCallback(async () => {
    try {
      const resp = await axios.get(`${BASE_URL}/webhooks`, { withCredentials: true });
      if (resp.data?.data) setWebhooks(resp.data.data);
    } catch { setErrorMsg(t('screens.webhooks.failedLoad')); } finally { setLoading(false); }
  }, [t]);

  useEffect(() => { fetchWebhooks(); }, [fetchWebhooks]);

  const toggleEvent = (event) => setSelectedEvents(prev => prev.includes(event) ? prev.filter(e => e !== event) : [...prev, event]);

  const handleCreate = async () => {
    if (!newName.trim() || !newUrl.trim() || selectedEvents.length === 0) return;
    setSubmitting(true);
    try {
      const resp = await axios.post(`${BASE_URL}/webhooks`, {
        name: newName, url: newUrl, events: selectedEvents,
      }, { withCredentials: true });
      if (resp.data?.data) setWebhooks(prev => [...prev, resp.data.data]);
    } catch {
      /* error — do not add mock webhook */
    } finally {
      setSubmitting(false);
      setShowNewForm(false);
      setNewName(''); setNewUrl(''); setSelectedEvents([]);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${BASE_URL}/webhooks/${id}`, { withCredentials: true });
      setWebhooks(prev => prev.filter(w => w._id !== id));
    } catch {
      setErrorMsg(t('screens.webhooks.failedDelete'));
    }
  };

  const handleToggle = async (id, isActive) => {
    try {
      await axios.patch(`${BASE_URL}/webhooks/${id}`, { isActive: !isActive }, { withCredentials: true });
      setWebhooks(prev => prev.map(w => w._id === id ? { ...w, isActive: !w.isActive } : w));
    } catch {
      setErrorMsg(t('screens.webhooks.failedUpdate'));
    }
  };

  const activeCount = webhooks.filter(w => w.isActive).length;
  // Flatten all delivery logs
  const allDeliveries = webhooks.flatMap(w => (w.deliveryLog || []).map(d => ({ ...d, webhookName: w.name })));

  const STATUS_STYLE = {
    active: { label: t('screens.webhooks.statusActive'), bg: '#ecfdf5', color: '#10b981' },
    paused: { label: t('screens.webhooks.statusPaused'), bg: '#fffbeb', color: '#f59e0b' },
    error:  { label: t('screens.webhooks.statusError'),  bg: '#fef2f2', color: '#ef4444' },
  };

  const getStatus = (wh) => {
    if (!wh.isActive) return 'paused';
    if (wh.consecutiveFailures >= 5) return 'error';
    return 'active';
  };

  return (
    <AppLayout>
      <div style={{ flex: 1, overflow: 'auto', padding: '32px 40px' }}>
        {errorMsg && (
          <div style={{ marginBottom: 16, padding: '12px 16px', background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, color: '#b91c1c', fontSize: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            {errorMsg}
            <button onClick={() => setErrorMsg('')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#b91c1c', fontSize: 18, lineHeight: 1 }}>×</button>
          </div>
        )}
        <PageHeader
          title={t('screens.webhooks.title')}
          subtitle={t('screens.webhooks.subtitle')}
          actions={
            <div style={{ display: 'flex', gap: 10 }}>
              <BtnSecondary><Icon name="file-text" size={15} />{t('screens.webhooks.eventCatalog')}</BtnSecondary>
              <BtnPrimary onClick={() => setShowNewForm(true)}><Icon name="plus" size={15} />{t('screens.webhooks.addWebhook')}</BtnPrimary>
            </div>
          }
        />

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 28 }}>
          {[
            { label: t('screens.webhooks.activeWebhooks'), value: activeCount, color: '#10b981' },
            { label: t('screens.webhooks.totalWebhooks'), value: webhooks.length, color: '#3b82f6' },
            { label: t('screens.webhooks.deliveryLogEntries'), value: allDeliveries.length, color: '#8b5cf6' },
            { label: t('screens.webhooks.failing'), value: webhooks.filter(w => w.consecutiveFailures >= 5).length, color: '#ef4444' },
          ].map((stat, i) => (
            <div key={i} style={{ background: '#fff', border: `1px solid ${S.borderLight}`, borderRadius: 10, padding: '18px 24px', textAlign: 'center' }}>
              <div style={{ fontSize: 22, fontWeight: 700, color: stat.color }}>{stat.value}</div>
              <div style={{ fontSize: 12, color: S.textMuted, marginTop: 4 }}>{stat.label}</div>
            </div>
          ))}
        </div>

        <div style={{ display: 'flex', borderBottom: `2px solid ${S.borderLight}`, marginBottom: 24 }}>
          {[{ id: 'webhooks', label: t('screens.webhooks.tabWebhooks') }, { id: 'deliveries', label: t('screens.webhooks.tabDeliveries') }].map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              style={{ padding: '10px 24px', border: 'none', background: 'none', cursor: 'pointer', fontSize: 14, fontWeight: activeTab === tab.id ? 600 : 400, color: activeTab === tab.id ? S.burgundy : S.textSecondary, borderBottom: activeTab === tab.id ? `2px solid ${S.burgundy}` : '2px solid transparent', marginBottom: -2, transition: 'all 0.15s' }}>
              {tab.label}
            </button>
          ))}
        </div>

        {activeTab === 'webhooks' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {loading ? <div style={{display:'flex',justifyContent:'center',alignItems:'center',padding:60}}><style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style><div style={{width:36,height:36,border:'3px solid #e2e8f0',borderTopColor:'#7c3aed',borderRadius:'50%',animation:'spin 0.8s linear infinite'}}/></div> : webhooks.map(wh => {
              const st = STATUS_STYLE[getStatus(wh)];
              return (
                <div key={wh._id} style={{ background: '#fff', border: `1px solid ${S.borderLight}`, borderRadius: 12, padding: 24 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 }}>
                        <span style={{ fontWeight: 600, color: S.textPrimary, fontSize: 15 }}>{wh.name}</span>
                        <span style={{ padding: '2px 9px', borderRadius: 20, fontSize: 11, fontWeight: 500, background: st.bg, color: st.color }}>{st.label}</span>
                        {wh.consecutiveFailures > 0 && <span style={{ fontSize: 11, color: '#ef4444' }}>{t('screens.webhooks.consecutiveFailures', { count: wh.consecutiveFailures })}</span>}
                      </div>
                      <code style={{ fontSize: 12, fontFamily: 'monospace', color: S.textMuted, background: S.bgCard, padding: '3px 8px', borderRadius: 4 }}>{wh.url}</code>
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button onClick={() => handleToggle(wh._id, wh.isActive)} style={{ padding: '7px 14px', border: `1px solid ${S.borderLight}`, borderRadius: 7, background: '#fff', fontSize: 13, cursor: 'pointer', color: S.textSecondary }}>
                        {wh.isActive ? t('screens.webhooks.pause') : t('screens.webhooks.enable')}
                      </button>
                      <button onClick={() => handleDelete(wh._id)} style={{ padding: '7px 14px', border: '1px solid #fca5a540', borderRadius: 7, background: '#fff', fontSize: 13, cursor: 'pointer', color: '#ef4444' }}>{t('screens.webhooks.delete')}</button>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 32, flexWrap: 'wrap' }}>
                    <div>
                      <div style={{ fontSize: 11, color: S.textMuted, fontWeight: 500, marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{t('screens.webhooks.eventsLabel')}</div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        {(wh.events || []).map(ev => (
                          <span key={ev} style={{ fontSize: 11, padding: '3px 8px', background: `${S.burgundy}10`, color: S.burgundy, borderRadius: 4, fontFamily: 'monospace' }}>{ev}</span>
                        ))}
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: 11, color: S.textMuted, fontWeight: 500, marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{t('screens.webhooks.deliveriesLogged')}</div>
                      <div style={{ fontSize: 13, color: S.textSecondary }}>{(wh.deliveryLog || []).length}</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {activeTab === 'deliveries' && (
          <div style={{ background: '#fff', border: `1px solid ${S.borderLight}`, borderRadius: 10, overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: S.bgCard }}>
                  <TH>{t('screens.webhooks.colWebhook')}</TH><TH>{t('screens.webhooks.colEvent')}</TH><TH>{t('screens.webhooks.colTimestamp')}</TH><TH>{t('screens.webhooks.colStatus')}</TH><TH>{t('screens.webhooks.colHttpCode')}</TH><TH>{t('screens.webhooks.colDuration')}</TH>
                </tr>
              </thead>
              <tbody>
                {allDeliveries.length === 0 ? (
                  <tr><td colSpan={6} style={{ padding: 40, textAlign: 'center', color: S.textMuted }}>{t('screens.webhooks.noDeliveries')}</td></tr>
                ) : allDeliveries.map((log, i) => (
                  <tr key={i} style={{ borderTop: `1px solid ${S.borderLight}`, background: i % 2 === 0 ? '#fff' : S.bgCard }}>
                    <TD><span style={{ fontSize: 13, color: S.textSecondary }}>{log.webhookName}</span></TD>
                    <TD><code style={{ fontSize: 11, fontFamily: 'monospace', color: S.burgundy, background: `${S.burgundy}10`, padding: '2px 6px', borderRadius: 3 }}>{log.event}</code></TD>
                    <TD><span style={{ fontSize: 12, color: S.textMuted }}>{log.deliveredAt ? new Date(log.deliveredAt).toLocaleString() : '—'}</span></TD>
                    <TD>
                      <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 500, background: log.success ? '#ecfdf5' : '#fef2f2', color: log.success ? '#10b981' : '#ef4444' }}>
                        {log.success ? t('screens.webhooks.deliverySuccess') : t('screens.webhooks.deliveryFailed')}
                      </span>
                    </TD>
                    <TD><span style={{ fontFamily: 'monospace', fontSize: 13, color: log.statusCode < 300 ? '#10b981' : '#ef4444' }}>{log.statusCode || '—'}</span></TD>
                    <TD><span style={{ fontSize: 12, color: S.textSecondary }}>{log.duration ? `${log.duration}ms` : '—'}</span></TD>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {showNewForm && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ background: '#fff', borderRadius: 14, padding: 32, width: 540, boxShadow: '0 25px 60px rgba(0,0,0,0.2)', maxHeight: '90vh', overflow: 'auto' }}>
              <div style={{ fontSize: 18, fontWeight: 700, color: S.textPrimary, marginBottom: 24 }}>{t('screens.webhooks.addWebhook')}</div>
              {[
                { label: t('screens.webhooks.nameLabel'), field: 'name', placeholder: t('screens.webhooks.namePh'), mono: false },
                { label: t('screens.webhooks.endpointUrl'), field: 'url', placeholder: t('screens.webhooks.endpointUrlPh'), mono: true },
              ].map(f => (
                <div key={f.field} style={{ marginBottom: 16 }}>
                  <label style={{ fontSize: 13, fontWeight: 500, color: S.textSecondary, display: 'block', marginBottom: 6 }}>{f.label}</label>
                  <input value={f.field === 'name' ? newName : newUrl} onChange={e => f.field === 'name' ? setNewName(e.target.value) : setNewUrl(e.target.value)} placeholder={f.placeholder}
                    style={{ width: '100%', padding: '10px 12px', border: `1px solid ${S.borderLight}`, borderRadius: 8, fontSize: 14, outline: 'none', boxSizing: 'border-box', fontFamily: f.mono ? 'monospace' : 'inherit' }} />
                </div>
              ))}
              <div style={{ marginBottom: 24 }}>
                <label style={{ fontSize: 13, fontWeight: 500, color: S.textSecondary, display: 'block', marginBottom: 8 }}>{t('screens.webhooks.subscribeToEvents')}</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {ALL_EVENTS.map(ev => (
                    <button key={ev} onClick={() => toggleEvent(ev)}
                      style={{ padding: '5px 10px', border: `1px solid ${selectedEvents.includes(ev) ? S.burgundy : S.borderLight}`, borderRadius: 6, background: selectedEvents.includes(ev) ? `${S.burgundy}10` : '#fff', fontSize: 12, cursor: 'pointer', color: selectedEvents.includes(ev) ? S.burgundy : S.textSecondary, fontFamily: 'monospace', transition: 'all 0.15s' }}>
                      {ev}
                    </button>
                  ))}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                <BtnSecondary onClick={() => { setShowNewForm(false); setSelectedEvents([]); setNewUrl(''); setNewName(''); }}>{t('screens.webhooks.cancel')}</BtnSecondary>
                <BtnPrimary onClick={handleCreate} disabled={!newName.trim() || !newUrl.trim() || selectedEvents.length === 0 || submitting}>
                  {submitting ? t('screens.webhooks.creating') : t('screens.webhooks.createWebhook')}
                </BtnPrimary>
              </div>
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  );
};

export default WebhooksScreen;
